// src/components/TeamModal.jsx
import React, { useEffect, useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";

const TRANSITION_MS = 220; // keep in sync with CSS

export const TeamModal = ({ isOpen, onClose, member }) => {
  // Render the modal only when needed (so we can animate out).
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isActive, setIsActive] = useState(false); // toggles CSS class

  // When isOpen becomes true, mount then activate on next frame (for CSS transition).
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    }
  }, [isOpen]);

  // After mounting, toggle the "active" flag so CSS can animate in.
  useLayoutEffect(() => {
    if (shouldRender) {
      // next frame so initial styles apply first
      const id = requestAnimationFrame(() => setIsActive(true));
      return () => cancelAnimationFrame(id);
    }
  }, [shouldRender]);

  // Body scroll lock + ESC close (only while visible)
  useEffect(() => {
    if (!shouldRender) return;
    const onEsc = (e) => e.key === "Escape" && handleRequestClose();
    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [shouldRender]);

  // If parent toggles isOpen=false, animate out then unmount
  useEffect(() => {
    if (!isOpen && shouldRender) {
      setIsActive(false);
      const t = setTimeout(() => setShouldRender(false), TRANSITION_MS);
      return () => clearTimeout(t);
    }
  }, [isOpen, shouldRender]);

  if (!shouldRender || !member) return null;

  const handleRequestClose = () => {
    // play exit animation, then ask parent to close (unmounts)
    setIsActive(false);
    setTimeout(() => onClose?.(), TRANSITION_MS);
  };

  const { name, job, img, bio, education = [], extra = [] } = member;

  const modalUI = (
    <div
      className={`team-modal ${isActive ? "is-active" : ""}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="team-modal__backdrop"
        aria-hidden="true"
        onClick={handleRequestClose}
      />
      <div className="team-modal__dialog">
        <button
          className="team-modal__close"
          onClick={handleRequestClose}
          aria-label="Fechar"
          title="Fechar"
        >
          &times;
        </button>

        <div className="team-modal__grid">
          <div className="team-modal__media">
            <img src={img} alt={name} />
          </div>
          <div className="team-modal__content">
            <h3 className="team-modal__title">{name}</h3>
            {job && <p className="team-modal__role">{job}</p>}
            {bio && <p className="team-modal__bio">{bio}</p>}

            {job === "Administrativa" && (
              <>
                {extra.length > 0 && (
                  <>
                    <ul className="team-modal__list">
                      {extra.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </>
                )}
                {education.length > 0 && (
                  <>
                    <h4 className="team-modal__heading">Formação Académica</h4>
                    <ul className="team-modal__list">
                      {education.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            {job !== "Administrativa" && (
              <>
                {education.length > 0 && (
                <>
                  <h4 className="team-modal__heading">Formação Académica</h4>
                  <ul className="team-modal__list">
                    {education.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </>
              )}

              {extra.length > 0 && (
                <>
                  <h4 className="team-modal__heading">Outros</h4>
                  <ul className="team-modal__list">
                    {extra.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </>
              )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalUI, document.body);
};
